// GIF API — provider-agnostic adapter. Supports Klipy (primary) and Tenor (legacy,
// shut down by Google in 2026).
//
//   Klipy: key goes in the URL path. Trending + search + suggestions.
//   Tenor: key is a query param. Featured + search + autocomplete.

export type Provider = "klipy" | "tenor";

export interface GifResult {
  id: string;
  title: string;
  previewUrl: string; // thumbnail shown in the grid
  fullUrl: string;    // full GIF sent on select / shown in preview
}

export interface GifPage {
  items: GifResult[];
  nextCursor: string | null;
}

const GIF_API_KEY = process.env.REACT_APP_GIF_API_KEY || process.env.REACT_APP_TENOR_API_KEY || "";
export const GIF_PROVIDER: Provider = process.env.REACT_APP_GIF_PROVIDER === "tenor" ? "tenor" : "klipy";
export const GIF_API_AVAILABLE = !!GIF_API_KEY;
export const GIF_PROVIDER_NAME = GIF_PROVIDER === "tenor" ? "Tenor" : "Klipy";

const PAGE_SIZE = 30;

// Response cache so the picker can re-open (or the user can toggle tabs back and
// forth) without re-hitting the provider. Capped LRU — oldest entry drops when
// full to keep memory bounded during long sessions.
const PAGE_CACHE_MAX = 24;
const pageCache = new Map<string, GifPage>();

function cacheGet(key: string): GifPage | undefined {
  const hit = pageCache.get(key);
  if (!hit) return undefined;
  pageCache.delete(key);
  pageCache.set(key, hit);
  return hit;
}

function cacheSet(key: string, value: GifPage): void {
  if (pageCache.size >= PAGE_CACHE_MAX) {
    const oldest = pageCache.keys().next().value;
    if (oldest !== undefined) pageCache.delete(oldest);
  }
  pageCache.set(key, value);
}

function pageCacheKey(searchTerm: string, cursor: string | null): string {
  // JSON.stringify avoids delimiter collisions between user-typed terms like "a|b".
  return JSON.stringify([GIF_PROVIDER, searchTerm, cursor]);
}

async function fetchKlipyPage(searchTerm: string, cursor: string | null, signal: AbortSignal): Promise<GifPage> {
  const base = `https://api.klipy.com/api/v1/${GIF_API_KEY}/gifs`;
  const endpoint = searchTerm ? "search" : "trending";
  const params = new URLSearchParams({ per_page: String(PAGE_SIZE) });
  if (searchTerm) params.set("q", searchTerm);
  if (cursor) params.set("page", cursor);
  const resp = await fetch(`${base}/${endpoint}?${params}`, { signal });
  if (!resp.ok) throw new Error(`Klipy API ${resp.status}`);
  const json = await resp.json();
  const items: any[] = json?.data?.data || [];
  const currentPage = Number(json?.data?.current_page || 1);
  const lastPage = Number(json?.data?.last_page || currentPage);
  const nextCursor = currentPage < lastPage ? String(currentPage + 1) : null;
  return {
    items: items.map((item: any): GifResult => ({
      id: String(item.id),
      title: item.title || "",
      previewUrl: item.file?.sm?.gif?.url || item.file?.xs?.gif?.url || item.file?.md?.gif?.url || "",
      fullUrl: item.file?.hd?.gif?.url || item.file?.md?.gif?.url || item.file?.sm?.gif?.url || "",
    })).filter((g) => g.previewUrl),
    nextCursor,
  };
}

async function fetchTenorPage(searchTerm: string, cursor: string | null, signal: AbortSignal): Promise<GifPage> {
  const base = "https://tenor.googleapis.com/v2";
  const endpoint = searchTerm ? "search" : "featured";
  const params = new URLSearchParams({
    key: GIF_API_KEY,
    client_key: "smeet_messenger",
    limit: String(PAGE_SIZE),
    media_filter: "tinygif,gif",
  });
  if (searchTerm) params.set("q", searchTerm);
  if (cursor) params.set("pos", cursor);
  const resp = await fetch(`${base}/${endpoint}?${params}`, { signal });
  if (!resp.ok) throw new Error(`Tenor API ${resp.status}`);
  const json = await resp.json();
  const items: any[] = json?.results || [];
  const rawNext = json?.next;
  // Tenor returns "0" to signal "end of results" rather than null.
  const nextCursor = rawNext && rawNext !== "0" && rawNext !== "" ? String(rawNext) : null;
  return {
    items: items.map((item: any): GifResult => ({
      id: String(item.id),
      title: item.title || "",
      previewUrl: item.media_formats?.tinygif?.url || item.media_formats?.nanogif?.url || "",
      fullUrl: item.media_formats?.gif?.url || item.media_formats?.tinygif?.url || "",
    })).filter((g) => g.previewUrl),
    nextCursor,
  };
}

export async function fetchGifPage(searchTerm: string, cursor: string | null, signal: AbortSignal): Promise<GifPage> {
  if (!GIF_API_KEY) return { items: [], nextCursor: null };
  const key = pageCacheKey(searchTerm, cursor);
  const cached = cacheGet(key);
  if (cached) return cached;
  const page = GIF_PROVIDER === "tenor"
    ? await fetchTenorPage(searchTerm, cursor, signal)
    : await fetchKlipyPage(searchTerm, cursor, signal);
  // Don't cache empty responses — they may be transient (rate limit, soft error)
  // and would otherwise leave the user stuck on "No GIFs found" until LRU evicts.
  if (page.items.length > 0) cacheSet(key, page);
  return page;
}

export async function fetchAutocomplete(query: string, signal: AbortSignal): Promise<string[]> {
  const term = query.trim();
  if (!GIF_API_KEY || !term) return [];
  try {
    if (GIF_PROVIDER === "tenor") {
      const url = `https://tenor.googleapis.com/v2/autocomplete?key=${GIF_API_KEY}&client_key=smeet_messenger&q=${encodeURIComponent(term)}&limit=6`;
      const resp = await fetch(url, { signal });
      if (!resp.ok) return [];
      const json = await resp.json();
      const arr: unknown = json?.results;
      return Array.isArray(arr) ? arr.slice(0, 6).map(String) : [];
    }
    // Klipy: /gifs/suggestions?q=... — the response shape isn't strictly documented,
    // so we accept both {data:{data:[...]}} and {data:[...]} defensively.
    const url = `https://api.klipy.com/api/v1/${GIF_API_KEY}/gifs/suggestions?q=${encodeURIComponent(term)}`;
    const resp = await fetch(url, { signal });
    if (!resp.ok) return [];
    const json = await resp.json();
    const raw: unknown = json?.data?.data ?? json?.data ?? json?.suggestions;
    if (!Array.isArray(raw)) return [];
    return raw.slice(0, 6).map((x: any) => (typeof x === "string" ? x : x?.name || x?.text || x?.query || "")).filter(Boolean);
  } catch (e: any) {
    // Re-throw aborts so a stale response can't wipe the fresh suggestions the new
    // request is about to set — the caller filters AbortError.
    if (e?.name === "AbortError") throw e;
    console.debug("GIF autocomplete failed:", e);
    return [];
  }
}
